//Libs
import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import XMarkIcon from "@heroicons/react/24/solid/XMarkIcon";

//Local
import BaseModal from "./BaseModal";
import BaseButtonPrimary from "./BaseButtonPrimary";
import BaseButtonSecondary from "./BaseButtonSecondary";
import BaseInputTextArea from "./BaseInputTextArea";
import { ExistingPurchaseOrder } from "../models/purchase-order";
import { DbWrite } from "../database";
import { useAuthStore } from "../store/firebase-auth";
import { useToastMessageStore } from "../store/toast-messages";
import { createToastMessageID } from "../utils";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  purchaseOrder: ExistingPurchaseOrder;
  siteKey: string;
}

interface FormData {
  notes: string;
  internalNotes: string;
}

export default function EditNotesDialog(props: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const firebaseUser = useAuthStore((state) => state.firebaseUser);
  const addToastMessage = useToastMessageStore(
    (state) => state.addToastMessage,
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      notes: props.purchaseOrder.notes || "",
      internalNotes: props.purchaseOrder.internalNotes || "",
    },
  });

  const onSubmit = async (data: FormData) => {
    if (!firebaseUser) return;

    setIsSubmitting(true);
    try {
      await DbWrite.purchaseOrders.update({
        id: props.purchaseOrder.id,
        refPath: props.purchaseOrder.refPath,
        notes: data.notes,
        internalNotes: data.internalNotes || null,
        lastModifiedBy: firebaseUser.uid,
      });

      addToastMessage({
        id: createToastMessageID(),
        message: "Notes updated successfully",
        dialog: false,
        type: "success",
      });

      props.onClose();
    } catch (error) {
      console.error("Error updating notes:", error);
      addToastMessage({
        id: createToastMessageID(),
        message: "Error updating notes",
        dialog: false,
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <BaseModal
      open={props.isOpen}
      closeModal={props.onClose}
      title={
        <div className="mb-4 flex w-full items-center justify-between rounded-t-lg bg-primary p-8 text-left text-white">
          <h1 className="inline-flex items-center text-xl font-semibold">
            Edit Notes
          </h1>
          <button type="button" onClick={props.onClose}>
            <XMarkIcon aria-label="close dialog" className="h-6 text-white" />
          </button>
        </div>
      }
      parentDivStyles="inline-block transform overflow-hidden max-w-screen-md rounded-lg bg-white text-left align-middle shadow-xl transition-all"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 p-8">
        <div>
          <Controller
            name="notes"
            control={control}
            render={({ field }) => (
              <BaseInputTextArea
                text="Vendor Notes"
                inputName="notes"
                admin={true}
                required={false}
                rows={4}
                value={field.value}
                onChange={field.onChange}
                placeholder="Notes visible to customer..."
              />
            )}
          />
          {errors.notes && (
            <div className="mt-1 text-sm text-red-500">
              {errors.notes.message}
            </div>
          )}
        </div>

        <div>
          <Controller
            name="internalNotes"
            control={control}
            render={({ field }) => (
              <BaseInputTextArea
                text="Internal Notes"
                inputName="internalNotes"
                admin={true}
                required={false}
                rows={4}
                value={field.value || ""}
                onChange={field.onChange}
                placeholder="Internal notes (not visible to customer)..."
              />
            )}
          />
          {errors.internalNotes && (
            <div className="mt-1 text-sm text-red-500">
              {errors.internalNotes.message}
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-4">
          <BaseButtonSecondary
            type="button"
            onClick={props.onClose}
            className="px-6 py-2"
          >
            Cancel
          </BaseButtonSecondary>
          <BaseButtonPrimary
            type="submit"
            disabled={isSubmitting}
            isBusy={isSubmitting}
            className="px-6 py-2"
          >
            Save Changes
          </BaseButtonPrimary>
        </div>
      </form>
    </BaseModal>
  );
}
